import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConstantsService } from '../../fidelidade/ConstantsService';
import { RotaGuardClient } from '../../guards/auth-loja.guard';
import { AutorizacaoService } from '../../services/autorizacao.service';
import { ChoiceGroupVM, LibraryGroupVM, ProductCatalogItemVM, ProductEditorVM } from '../produtos-v2.models';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';
import { TelaEditorAdicionalV2Component } from '../tela-editor-adicional-v2/tela-editor-adicional-v2.component';
import { NotificationService } from '../../services/notification.service';

type GlobalAdditionalOptionModeV2 = 'review' | 'activation' | 'batch-price';

type EstadoSalvamentoV2 = 'hidden' | 'dirty' | 'saving' | 'saved';

interface ResultadoVinculoV2 {
  product: ProductCatalogItemVM;
  ok: boolean;
  error?: string;
}

@Component({
  selector: 'app-tela-editor-adicional-global-v2',
  templateUrl: './tela-editor-adicional-global-v2.component.html',
  styleUrls: ['./tela-editor-adicional-global-v2.component.scss']
})
export class TelaEditorAdicionalGlobalV2Component implements OnInit, OnDestroy {
  empresa: any;
  editor: ProductEditorVM | null = null;
  libraryItem: LibraryGroupVM | null = null;
  group: ChoiceGroupVM | null = null;
  loading = true;
  errorMessage = '';
  requestedOptionMode: GlobalAdditionalOptionModeV2 = 'review';
  sharedProductNames: string[] = [];
  idsVinculados: Array<number | string> = [];

  /**
   * O editor calcula este estado e emite por `actionBarStateChange`, mas até aqui
   * quem desenhava era só o editor de produto — em tela cheia o auto-save rodava
   * sem dizer nada.
   */
  estadoSalvamento: EstadoSalvamentoV2 = 'hidden';

  @ViewChild(TelaEditorAdicionalV2Component) editorAdicional?: TelaEditorAdicionalV2Component;

  seletorAberto = false;
  vinculando = false;
  resultados: ResultadoVinculoV2[] = [];
  private vinculosAntes = 0;

  private empresaSubscription?: Subscription;
  private paramsSubscription?: Subscription;
  private querySubscription?: Subscription;
  private loadSequence = 0;

  constructor(
    private constantsService: ConstantsService,
    private facade: ProdutosV2FacadeService,
    private route: ActivatedRoute,
    private router: Router,
    private autorizacaoService: AutorizacaoService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.empresaSubscription = this.constantsService.empresa$.subscribe((empresa) => {
      if (!empresa?.catalogo?.id) return;
      this.empresa = empresa;
      void this.load();
    });

    this.paramsSubscription = this.route.params.subscribe(() => {
      if (this.empresa) void this.load();
    });

    this.querySubscription = this.route.queryParams.subscribe((params) => {
      const mode = params['modoOpcoes'];
      this.requestedOptionMode = mode === 'activation' || mode === 'batch-price' ? mode : 'review';
    });
  }

  ngOnDestroy(): void {
    this.empresaSubscription?.unsubscribe();
    this.paramsSubscription?.unsubscribe();
    this.querySubscription?.unsubscribe();
  }

  get canEditStructure(): boolean {
    return !this.empresa?.id || !this.empresa?.catalogo?.compartilhado;
  }

  get canEditPrices(): boolean {
    return this.canEditStructure
      || (!!this.empresa?.catalogo?.precoPorEmpresa
        && RotaGuardClient.alterarPrecoCardapioCompartilhado(this.autorizacaoService.getUser()));
  }

  get canEditAvailability(): boolean {
    return this.canEditStructure || !!this.empresa?.catalogo?.disponibilidadePorEmpresa;
  }

  get canEditOptionDisplay(): boolean {
    return RotaGuardClient.podeEditarExibicaoOpcoes(this.autorizacaoService.getUser(), this.empresa);
  }

  get usageLabel(): string {
    const count = this.sharedProductNames.length || Number(this.libraryItem?.sharedCount || 0);
    if (count === 0) return 'Sem vínculo';
    if (count === 1) return 'Exclusivo';
    return `Compartilhado · ${count} produtos`;
  }

  // ── Estado de salvamento ──

  onEstadoSalvamento(estado: { creationPhase: 'config' | 'full'; saveFooterState: EstadoSalvamentoV2 }): void {
    this.estadoSalvamento = estado.saveFooterState;
  }

  get rotuloSalvamento(): string {
    if (this.estadoSalvamento === 'saving') return 'Salvando...';
    if (this.estadoSalvamento === 'dirty') return 'Alterações não salvas';
    return 'Salvo';
  }

  get temAlteracaoPendente(): boolean {
    return this.estadoSalvamento === 'dirty';
  }

  /** O auto-save já grava sozinho; este botão é para quem não quer esperar. */
  salvarAgora(): void {
    void this.editorAdicional?.onSave();
  }

  // ── Vincular a mais produtos ──

  get iconeVinculo(): string {
    if (!this.sharedProductNames.length) return 'fa-unlink';
    return this.sharedProductNames.length > 1 ? 'fa-link' : 'fa-lock';
  }

  get tituloVinculo(): string {
    if (!this.sharedProductNames.length) return 'Sem vínculo';
    return this.sharedProductNames.length > 1 ? 'Alterações compartilhadas' : 'Adicional exclusivo';
  }

  get textoVinculo(): string {
    if (!this.sharedProductNames.length) {
      return 'Este adicional ainda não aparece em nenhum produto. Vincule para o cliente poder escolhê-lo.';
    }
    return this.sharedProductNames.length > 1
      ? 'Editar este grupo atualiza todos os produtos abaixo.'
      : 'Este grupo está vinculado somente ao produto abaixo.';
  }

  get resultadoVisivel(): boolean {
    return this.resultados.length > 0;
  }

  get vinculadosComSucesso(): number {
    return this.resultados.filter((resultado) => resultado.ok).length;
  }

  get vinculosFalhos(): ResultadoVinculoV2[] {
    return this.resultados.filter((resultado) => !resultado.ok);
  }

  /** Cruzar de 1 para 2 muda o significado do grupo; o operador precisa saber. */
  get virouCompartilhado(): boolean {
    return this.vinculosAntes <= 1 && this.sharedProductNames.length > 1;
  }

  abraSeletor(): void {
    if (!this.canEditStructure) {
      this.notificationService.showError('A estrutura dos adicionais é controlada pelo catálogo da rede.');
      return;
    }
    this.resultados = [];
    this.seletorAberto = true;
  }

  fecheSeletor(): void {
    if (this.vinculando) return;
    this.seletorAberto = false;
    this.resultados = [];
  }

  voltarAoSeletor(): void {
    this.resultados = [];
  }

  async vinculeAosProdutos(produtos: ProductCatalogItemVM[]): Promise<void> {
    if (!produtos.length || !this.group || this.vinculando) return;
    this.vinculosAntes = this.sharedProductNames.length;
    this.resultados = [];
    await this.executeVinculos(produtos);
  }

  async tenteVinculosFalhos(): Promise<void> {
    const pendentes = this.vinculosFalhos.map((resultado) => resultado.product);
    if (!pendentes.length) return;
    this.resultados = this.resultados.filter((resultado) => resultado.ok);
    await this.executeVinculos(pendentes);
  }

  private async executeVinculos(produtos: ProductCatalogItemVM[]): Promise<void> {
    if (!this.libraryItem) return;

    this.vinculando = true;
    try {
      // Sequencial de propósito: o servidor grava adicional por produto em transação
      // própria, e em paralelo uma falha some no meio das outras.
      for (const produto of produtos) {
        try {
          await this.facade.vinculeGrupoDaBiblioteca(
            this.empresa.catalogo,
            produto.raw || { id: produto.id },
            this.libraryItem,
            false
          );
          this.resultados.push({ product: produto, ok: true });
        } catch (error: any) {
          this.resultados.push({ product: produto, ok: false, error: error?.message || 'Falha ao vincular.' });
        }
      }
    } finally {
      this.vinculando = false;
    }

    await this.recarregueVinculos();
  }

  private async recarregueVinculos(): Promise<void> {
    const additionalId = this.route.snapshot.params['id'];
    try {
      const vinculados = await this.facade.carregueProdutosDoAdicional(this.empresa.catalogo, additionalId);
      this.sharedProductNames = vinculados.map((produto) => produto.nome);
      this.idsVinculados = vinculados.map((produto) => produto.id);
      this.atualizeContagemDeUso();
    } catch {
      // A gravação já aconteceu; se a releitura falhar, o número volta no próximo load.
    }
  }

  private atualizeContagemDeUso(): void {
    const total = this.sharedProductNames.length;
    if (this.group) {
      this.group.shared = total > 1;
      this.group.sharedCount = total;
    }
    if (this.libraryItem) {
      this.libraryItem.sharedCount = total;
      this.libraryItem.shared = total > 1;
      this.libraryItem.sharedProductNames = [...this.sharedProductNames];
    }
  }

  get isRequired(): boolean {
    return Number(this.group?.min || 0) > 0;
  }

  async load(): Promise<void> {
    const sequence = ++this.loadSequence;
    this.loading = true;
    this.errorMessage = '';
    this.editor = null;
    this.group = null;

    try {
      const additionalId = this.route.snapshot.params['id'];
      await this.facade.carregueCatalogo(this.empresa);
      const library = await this.facade.carregueBiblioteca(this.empresa.catalogo);
      if (sequence !== this.loadSequence) return;

      this.libraryItem = library.find((item) => String(item.id) === String(additionalId)) || null;
      if (!this.libraryItem) {
        throw new Error('Este adicional não foi encontrado no catálogo.');
      }

      const requestedProductId = Number(this.route.snapshot.queryParams['produto'] || 0);
      const sourceProductId = requestedProductId
        || Number(this.libraryItem.sourceProductId || this.libraryItem.productId || 0);
      if (!sourceProductId) {
        throw new Error('Este adicional ainda não possui um produto de origem para edição.');
      }

      const templates = await this.facade.carregueTemplates(this.empresa.catalogo);
      const editor = await this.facade.carregueProduto(this.empresa, sourceProductId, templates);
      if (sequence !== this.loadSequence) return;

      let group = editor.groups.find((item) => String(item.id) === String(additionalId)) || null;
      if (!group && requestedProductId) {
        const fallbackId = Number(this.libraryItem.sourceProductId || this.libraryItem.productId || 0);
        if (fallbackId && fallbackId !== requestedProductId) {
          const fallbackEditor = await this.facade.carregueProduto(this.empresa, fallbackId, templates);
          group = fallbackEditor.groups.find((item) => String(item.id) === String(additionalId)) || null;
          if (group) this.editor = fallbackEditor;
        }
      }

      if (!group) {
        throw new Error('O produto de origem não contém mais este adicional.');
      }

      this.editor = this.editor || editor;
      this.group = group;
      const vinculados = await this.facade.carregueProdutosDoAdicional(this.empresa.catalogo, additionalId);
      this.sharedProductNames = vinculados.map((produto) => produto.nome);
      this.idsVinculados = vinculados.map((produto) => produto.id);
      const usageCount = this.sharedProductNames.length;
      this.group.shared = usageCount > 1;
      this.group.sharedCount = usageCount;
      this.group.required = Number(this.group.min || 0) > 0;
      this.libraryItem.sharedCount = usageCount;
      this.libraryItem.shared = usageCount > 1;
      this.libraryItem.sharedProductNames = [...this.sharedProductNames];
    } catch (error: any) {
      if (sequence === this.loadSequence) {
        this.errorMessage = error?.message || 'Não foi possível abrir este adicional.';
      }
    } finally {
      if (sequence === this.loadSequence) this.loading = false;
    }
  }

  onGroupSaved(group: ChoiceGroupVM): void {
    this.group = group;
    if (this.libraryItem) {
      this.libraryItem.name = group.name;
      this.libraryItem.min = group.min;
      this.libraryItem.max = group.max;
      this.libraryItem.required = Number(group.min || 0) > 0;
      this.libraryItem.optionsCount = group.options.length;
    }
  }

  onGroupDeleted(): void {
    this.goBack();
  }

  goBack(): void {
    const productId = Number(this.route.snapshot.queryParams['voltarProduto'] || 0);
    if (productId) {
      void this.router.navigate(['/admin/produtos-v2'], {
        queryParams: { produtoDrawer: productId, drawerTab: 'adicionais' }
      });
      return;
    }

    void this.router.navigate(['/admin/adicionais-v2']);
  }
}
